/**
 * Environment variable utilities
 *
 * Cross-runtime environment variable access for Cloudflare Workers,
 * Node.js, and browser environments.
 *
 * In Cloudflare's modules runtime (Workers and Pages Functions), secrets
 * and vars exist only on the `env` argument handed to each handler. Every
 * entry point (fetch/queue/scheduled handler, Durable Object constructor,
 * or the root Pages middleware) must call `setEnvSource(env)` before any
 * code path reaches `getEnv()`.
 */

/**
 * Module-scoped env source populated by `setEnvSource`. Holds only string
 * values; live bindings (R2 buckets, queues, Durable Object namespaces)
 * are filtered out at registration time.
 */
let envSource: Map<string, string> | null = null;

/**
 * Register the runtime `env` object as the primary source for `getEnv`.
 *
 * Call once at every entry point (worker fetch/queue/scheduled handlers,
 * Durable Object constructors, the root Pages Functions middleware).
 * Calling again replaces the previous source, so repeated invocations per
 * request are idempotent. Non-string values are skipped: bindings never
 * leak into env lookups and never shadow the fallback chain.
 *
 * This replaces the old `Object.assign(globalThis, env)` bridge, which
 * copied live bindings onto shared isolate global state and short-circuited
 * the fallback chain on non-string collisions (audit A2).
 *
 * @param env - The runtime env object (string vars plus bindings)
 */
export function setEnvSource(env: object): void {
  const source = new Map<string, string>();
  for (const [key, value] of Object.entries(env)) {
    if (typeof value === 'string') {
      source.set(key, value);
    }
  }
  envSource = source;
}

/**
 * Read environment variable from runtime.
 * Resolution order: registered env source (`setEnvSource`), then Node.js
 * `process.env`, then Vite `import.meta.env`. `globalThis` is intentionally
 * never consulted (audit A2).
 */
function readEnv(key: string): string | undefined {
  // Cloudflare Workers / Pages Functions: env registered per entry point
  const fromSource = envSource?.get(key);
  if (fromSource !== undefined) {
    return fromSource;
  }
  // Node.js
  if (typeof process !== 'undefined' && process.env?.[key]) {
    return process.env[key];
  }
  // Vite/browser - cast avoids requiring vite/client in worker tsconfigs
  if (typeof import.meta !== 'undefined') {
    const viteEnv = (import.meta as { env?: Record<string, string | undefined> }).env;
    const value = viteEnv?.[key];
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

/**
 * Get environment variable with validation.
 *
 * @param name - Environment variable name
 * @param required - If true, throws error when variable is missing (default: true)
 * @param defaultValue - Default value to use when required is false and variable is missing
 * @returns Environment variable value or default
 * @throws Error if variable is required but missing, or if defaultValue is provided when required is true
 *
 * @example
 * // Required variable (throws if missing)
 * const apiKey = getEnv('API_KEY');
 *
 * @example
 * // Optional variable with default
 * const port = getEnv('PORT', false, '3000');
 *
 * @example
 * // Optional variable (returns undefined if missing)
 * const debug = getEnv('DEBUG', false);
 */
export function getEnv(name: string, required?: true): string;
export function getEnv(name: string, required: false, defaultValue?: string): string | undefined;
export function getEnv(name: string, required = true, defaultValue?: string): string | undefined {
  // Validate parameters
  if (required && defaultValue !== undefined) {
    throw new Error(`getEnv('${name}'): cannot provide defaultValue when required is true`);
  }

  const value = readEnv(name);

  if (value === undefined) {
    if (required) {
      throw new Error(`Missing required environment variable: ${name}`);
    }
    return defaultValue;
  }

  return value;
}
